import { createFileRoute, redirect } from "@tanstack/react-router";
import LoadingPage from "lib/components/layout/LoadingPage";
import ShowroomCheckout from "lib/components/showroom2/pages/ShowroomCheckout";
import ShowroomConfigure from "lib/components/showroom2/pages/ShowroomConfigure";
import ShowroomPlanViewerOnly from "lib/components/showroom2/pages/ShowroomPlanViewerOnly";
import { ShowroomProvider } from "lib/components/showroom2/state/useShowroomState";
import { initializeShowroomWorker } from "lib/hooks/useShowroomWorkerBuilding";
import { errorPath, useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";

/**
 * Public buyer configurator for an unsaved plan. Loads a master / community
 * plan via the `public-plans` API and surfaces the CTA modal that creates a
 * new `HomePlan` on submit.
 */
function ShowroomConfigurePage() {
  const { builderLoading } = useUserContext();
  const { builderId, planId, lotId, page, showroomPlanViewerOnly } = useAppRouterParams();

  if (builderLoading) {
    return <LoadingPage pageTitle="Showroom" />;
  }

  const showroomKey = [builderId, planId, lotId].filter(Boolean).join("-") || undefined;
  const planViewerOnly = showroomPlanViewerOnly === "true";
  let pageContent = <ShowroomConfigure checkoutEnabled />;
  if (page === "checkout") {
    pageContent = <ShowroomCheckout />;
  }
  if (planViewerOnly) {
    pageContent = <ShowroomPlanViewerOnly />;
  }

  return (
    <ShowroomProvider key={showroomKey} version="sales">
      {pageContent}
    </ShowroomProvider>
  );
}

export const Route = createFileRoute("/showroom/")({
  // Gate at the route level so the check is tied to route resolution, not a
  // component effect that can fire mid-transition with stale `enabledFeatures`.
  // Defer until `UserContextSync` flips `flagsHydrated` and calls
  // `router.invalidate()`.
  beforeLoad: ({ context, search }) => {
    if (!context.flagsHydrated) {
      return;
    }
    if (!context.enabledFeatures.showroomUnification) {
      throw redirect({
        to: errorPath(403, undefined, "This feature is not enabled for your account."),
        replace: true,
      });
    }
    if (!context.enabledProducts.showroom) {
      throw redirect({
        to: errorPath(403, undefined, "The product you are trying to access is not enabled for this organization."),
        replace: true,
      });
    }

    const { renderSource } = search as { renderSource?: string };
    if (renderSource?.toLowerCase() !== "server") {
      initializeShowroomWorker({
        defaultEnabled: context.enabledFeatures.showroomWorkerBuildingGen ?? false,
      });
    }
  },
  component: ShowroomConfigurePage,
  staticData: { isPublicPage: true },
});
